<template>
  <div class="auth-wrapper">
    <div class="auth-container">
      <div class="auth-content-container">
        <v-card class="auth-card">
          <div class="auth-title mb-1">{{ $t("auth.reset_password") }}</div>
          <div class="auth-subtitle">{{ $t("auth.enter_email") }}</div>

          <VaDisclaimer
            v-if="success"
            class="success-disclaimer va-disclaimer--caption"
            :type="'success'"
            :icon-color="'success'"
            :text="$t('auth.reset_password_success')">
            <template #icon>
              <CheckCircleIcon></CheckCircleIcon>
            </template>
          </VaDisclaimer>

          <v-form ref="form" @submit.prevent="validate">
            <v-text-field
              :label="$t('auth.email')"
              class="mt-6"
              :placeholder="$t('auth.email')"
              :rules="[rules.required, rules.email]"
              v-model.trim="form.email"
              :disabled="success"
              type="email"
              required
              persistent-placeholder
              hide-details="auto"
              validate-on-blur>
              <template v-slot:message="{ message }">
                <v-icon size="16" color="error">{{ icons.mdiAlertCircle }}</v-icon>
                {{ message }}
              </template>
            </v-text-field>
            <v-btn
              v-if="!success"
              color="primary"
              :loading="loading"
              type="submit"
              x-large
              class="auth-btn spacing-top--24">
              {{ $t("auth.reset_password") }}
            </v-btn>
            <v-btn
              v-else
              color="primary"
              :disabled="successResend"
              :loading="loading"
              type="submit"
              x-large
              class="auth-btn spacing-top--24">
              {{ $t("auth.resend_email") }}
            </v-btn>
          </v-form>
          <div class="mt-3">
            <router-link to="/login">
              <div class="flex text-center back-to-login">
                <v-icon color="primary" size="14"> {{ icons.mdiChevronLeft }} </v-icon>
                <span class="auth-back">{{ $t("auth.back_to_login") }}</span>
              </div>
            </router-link>
          </div>
        </v-card>
      </div>
    </div>
  </div>
</template>

<script>
  import { mdiEyeOutline, mdiEyeOffOutline, mdiChevronLeft, mdiAlertCircle } from "@mdi/js";
  import useAppConfig from "@core/@app-config/useAppConfig";
  import { validateEmail } from "@/assets/utilities/helpers/email";
  import VaDisclaimer from "@/components/custom/VaDisclaimer.vue";
  import CheckCircleIcon from "@/components/icons/CheckCircleIcon.vue";

  export default {
    components: {
      VaDisclaimer,
      CheckCircleIcon,
    },
    data() {
      const { appSkinVariant } = useAppConfig();

      return {
        validateEmail,
        appSkinVariant,
        icons: {
          mdiEyeOutline: mdiEyeOutline,
          mdiEyeOffOutline: mdiEyeOffOutline,
          mdiChevronLeft: mdiChevronLeft,
          mdiAlertCircle: mdiAlertCircle,
        },
        form: {
          email: "",
        },
        rules: {
          required: value => !!value || this.$t("generic.validation.required"),
          email: value => !!validateEmail(value) || this.$t("generic.validation.email"),
        },
        success: false,
        successResend: false,
        errorMessage: "",
        loading: false,
      };
    },
    methods: {
      async validate() {
        if (this.$refs.form.validate()) {
          this.loading = true;
          this.errorMessage = true;
          /**
           * TODO: Redo this function
           * For now, using plain FETCH api to request password change.
           * That is because the endpoint for password reset currently doesn't return HYDRA response,
           * and the dataProvider (hydra) expects hydra response, otherwise fails to map error messages.
           */
          try {
            const resetPasswordEndpoint = `${process.env.VUE_APP_API_URL}/auth/reset_password`;

            const response = await fetch(resetPasswordEndpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(this.form),
            });

            const responseData = await response.json();

            if (responseData.result === "SUCCESS") {
              if (this.success === true) {
                this.successResend = true;
              } else {
                this.success = true;
              }

              return;
            }
            /**
             * Pure fetch API doesn't trigger catch block if the response status is an error status (4xx / 5xx).
             */
            this.errorMessage = responseData?.["hydra:description"];
            throw new Error(responseData?.["hydra:description"] || responseData.message);
          } catch (e) {
            this.$admin.toast.error(this.errorMessage || this.$t("generic.error"));
            throw new Error(e);
          } finally {
            this.loading = false;
          }
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/functions.scss";
  @import "@core/preset/preset/typography/index.scss";
  .success-disclaimer {
    margin: 16px 0 24px;
  }

  .auth-back {
    @include text-caption();
  }

  .back-to-login {
    &:hover {
      color: get-color("primary-hover");
    }
  }

  .auth-wrapper {
    width: 100%;
    @media (min-width: 600px) {
      margin-top: 32px;
    }
    a {
      text-decoration: unset;
    }
  }

  .auth-container {
    display: flex;
    justify-content: center;
    padding: 4px;
    @media (min-width: 600px) {
      padding: 0;
    }
    .auth-content-container {
      width: 100%;
      @media (min-width: 600px) {
        width: 398px;
      }
    }
  }

  .auth-card {
    padding: 24px;
    border-radius: 6px;
    @media (min-width: 600px) {
      padding: 32px;
    }

    .auth-title {
      @include heading-4();
      color: get-color("headline");
      margin-bottom: 4px;
    }
    .auth-subtitle {
      @include text-body-2();
      color: get-color("subtitle");
    }

    .auth-divider-wrapper {
      @include text-caption();
      align-items: center;
      display: flex;
      justify-content: space-between;
      margin: 24px 0;
      .auth-divider {
        height: 1px;
        border-radius: 4px;
        width: 35%;
        background: get-color("border");
      }
      @media (min-width: 600px) {
        margin: 32px 0;
      }
    }

    .auth-btn {
      @include text-button-medium();
      background-color: get-color("primary");
      width: 100%;
      color: white;
      padding: 12px 54px;
      border-radius: 4px;

      &:hover {
        background-color: get-color("primary-hover");
      }
    }
  }
</style>
